import {
  ChangeDetectionStrategy, Component, EventEmitter, HostListener,
  Input, Output, signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AbilitiesService } from '../../services/abilities.service';
import { AbilityRequest } from '../../models/ability-catalog.model';

interface TimeframeOption { value: string; label: string; }

/**
 * Request-access confirmation dialog — shared by the Abilities modal and the
 * /abilities page (per-card "Request access" and the no-match "Request …"
 * search flow). Confirms what is being requested and captures an optional
 * use-case + timeframe before POSTing via AbilitiesService.requestAbility,
 * then shows a brief "Request sent" confirmation. The parent owns visibility
 * (renders on demand, removes on (close)) and flips its own "Requested"
 * state when (sent) fires.
 */
@Component({
  selector: 'app-ability-request-dialog',
  standalone: true,
  imports: [FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './ability-request-dialog.component.html',
  styleUrls: ['./ability-request-dialog.component.scss'],
})
export class AbilityRequestDialogComponent {
  /** Existing gated ability being requested (null when requesting by term). */
  @Input() abilityId: string | null = null;
  @Input() abilityName = '';
  @Input() system = '';
  /** Free-text connector term (no-match search flow) — replaces the ability fields. */
  @Input() term: string | null = null;
  /** Where the request originated, e.g. "hub-catalog" | "hub-abilities-modal" | "hub-search". */
  @Input() source = 'hub-catalog';

  @Output() readonly close = new EventEmitter<void>();
  /** Fires once the API accepts the request — before the user dismisses the
   *  confirmation — so the parent can flip its "Requested" state immediately. */
  @Output() readonly sent = new EventEmitter<void>();

  // Same values + labels as app-request-ability-dialog's timeframe chips, for
  // visual/behavioral consistency across every request form on the platform.
  readonly timeframes: TimeframeOption[] = [
    { value: 'asap', label: 'As soon as possible' },
    { value: 'month', label: 'Within a month' },
    { value: 'quarter', label: 'This quarter' },
    { value: 'exploring', label: 'Just exploring' },
  ];

  readonly useCase = signal('');
  readonly timeframe = signal('exploring');
  readonly submitting = signal(false);
  readonly done = signal(false);
  readonly error = signal(false);

  constructor(private readonly abilitiesSvc: AbilitiesService) {}

  @HostListener('document:keydown.escape')
  onEscape(): void { if (!this.submitting()) this.onClose(); }

  onClose(): void { this.close.emit(); }

  send(): void {
    if (this.submitting() || this.done()) return;
    this.submitting.set(true);
    this.error.set(false);
    // use_case/timeframe ride along unconditionally — the API stores them when
    // present and harmlessly ignores them otherwise.
    const payload: AbilityRequest = {
      source: this.source,
      use_case: this.useCase().trim(),
      timeframe: this.timeframe(),
    };
    if (this.term) {
      payload.term = this.term;
      if (this.system) payload.system = this.system;
    } else {
      payload.ability_id = this.abilityId ?? undefined;
      payload.ability_name = this.abilityName;
      payload.system = this.system;
    }
    this.abilitiesSvc.requestAbility(payload).subscribe(ok => {
      this.submitting.set(false);
      if (ok) {
        this.done.set(true);
        this.sent.emit();
      } else {
        this.error.set(true);
      }
    });
  }
}
